/* eslint-disable complexity */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import R from "ramda";
import { Container, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import JoinModal from "components/JoinModal";
import CommonPlayer from "components/CommonPlayer";
import { useUsers, useJoinModal, useGlobalLoading } from "hooks";
import ShareModal from "components/ShareModal";
import StarringYouCards from "components/StarringYouCards";
import MakePresenter from "presenters/MakePresenter";
import MetaTags from "components/MetaTags";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import TagPresenter from "presenters/TagPresenter";
import UserPresenter from "presenters/UserPresenter";

import Actions from "./components/Actions";
import MakeMessage from "./components/MakeMessage";
import AppsPromo from "./components/AppsPromo";
import MakeMessageModal from "./components/MakeMessageModal";
import useStyles from "./useStyles";

const Make = (props) => {
  const classes = useStyles();
  const { templateGroup, make, loading, joinable, isMessageModalVisible, isShareModalVisible } = props;
  const { currentUser } = useUsers();
  const { joinModalVisible, openJoinModal, closeJoinModal } = useJoinModal();
  const theme = useTheme();
  const { reset } = useGlobalLoading();

  const [messageModalVisible, setMessageModalVisible] = useState(isMessageModalVisible);
  const [shareModalVisible, setShareModalVisible] = useState(isShareModalVisible);
  const [isAddInfoVisible, setAddInfoVisible] = useState(false);
  const [isCVPModalVisible, setCVPModalVisible] = useState(false);
  const [showCVPModalNext, setShowCVPModalNext] = useState(false);
  const [cvpModalCounter, setCVPModalCounter] = useState(0);

  const autoShowCVPModal = () => {
    if (cvpModalCounter < 2) {
      setCVPModalVisible(true);
      setCVPModalCounter(cvpModalCounter + 1);
    }
  };

  const handleMessageModalClose = () => setMessageModalVisible(false);
  const handleMessageModalOpen = () => setMessageModalVisible(true);

  const handleShareModalOpen = () => setShareModalVisible(true);
  const handleShareModalClose = () => {
    setShareModalVisible(false);
    if (showCVPModalNext) {
      setShowCVPModalNext(false);
      autoShowCVPModal();
    }
  };

  const handleShareButtonClick = () => {
    if (UserPresenter.isPaid(currentUser) || MakePresenter.isLive(make)) {
      handleShareModalOpen();
    } else {
      openJoinModal();
    }
  };

  const handleAddMessageButtonClick = () => {
    if (UserPresenter.isPaid(currentUser) || MakePresenter.isLive(make)) {
      handleMessageModalOpen();
    } else {
      openJoinModal();
    }
  };

  const primaryTag = TemplateGroupPresenter.primaryTag(templateGroup);
  const primaryTagName = TagPresenter.name(primaryTag);
  let message = "";
  if (make && !R.isNil(make.message)) {
    message = make.message;
  } else if (!R.isNil(TemplateGroupPresenter.defaultMakeMessage(templateGroup))) {
    message = TemplateGroupPresenter.defaultMakeMessage(templateGroup);
  }
  const title = TemplateGroupPresenter.defaultMakeMessage(templateGroup);
  const relatedTemplateGroups = TemplateGroupPresenter.relatedTemplateGroups(templateGroup);

  const makeMessage = MakePresenter.makeMessage(make);
  const isOwner = MakePresenter.isOwner(make);
  const showSuggested = !loading && !isOwner && relatedTemplateGroups?.length;

  useEffect(() => {
    if (isCVPModalVisible) {
      setAddInfoVisible(true);
    }
    return () => {
      reset();
    };
  }, [isCVPModalVisible]);

  return (
    <div
      className={classes.background}
      style={primaryTagName && { backgroundImage: theme.palette.makeBackgrounds[`${primaryTagName}`] }}
    >
      <Container
        className={clsx(classes.root, {
          [classes.rootToVertical]: TemplateGroupPresenter.isStarringYouVertical(templateGroup),
        })}
      >
        {!TemplateGroupPresenter.isStarringYouVertical(templateGroup) ? (
          <Typography align="center" variant="h1" color="neutral.main" classes={{ root: classes.title }}>
            {title}
          </Typography>
        ) : null}
        <div className={classes.player}>
          <CommonPlayer joinable={joinable} make={make} templateGroup={templateGroup} loading={loading} bordered />
        </div>

        {!loading && (
          <Actions
            isOwner={isOwner}
            make={make}
            templateGroup={templateGroup}
            isAddInfoVisible={isAddInfoVisible}
            isCVPModalVisible={isCVPModalVisible}
            setCVPModalVisible={setCVPModalVisible}
            onShareButtonClick={handleShareButtonClick}
            onAddMessageButtonClick={handleAddMessageButtonClick}
          />
        )}

        {!R.isNil(makeMessage) && (
          <MakeMessage onModalOpen={handleMessageModalOpen} makeMessage={makeMessage} isOwner={isOwner} />
        )}

        {!isOwner && <AppsPromo addSpacing={!R.isNil(makeMessage)} />}

        {showSuggested ? <StarringYouCards title="Suggested Ecards" items={relatedTemplateGroups} /> : null}
        <MetaTags>
          <meta name="keywords" content={`${TemplateGroupPresenter.tags(templateGroup)}, eCard`} />
          <meta name="description" content={message} />
          <meta name="og:title" content={`Check out '${TemplateGroupPresenter.name(templateGroup)}' on JibJab.com`} />
          <meta name="og:description" content={TemplateGroupPresenter.description(templateGroup)} />
          <meta name="og:image" content={make && make.shareThumbnail} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@jibjab" />
          <meta
            name="twitter:title"
            content={`Check out '${TemplateGroupPresenter.name(templateGroup)}' on JibJab.com`}
          />
          <meta name="twitter:description" content={TemplateGroupPresenter.description(templateGroup)} />
          <meta name="twitter:image" content={make && make.shareThumbnail} />
          <meta name="twitter:image:alt" content={`${TemplateGroupPresenter.name(templateGroup)} eCard Image`} />
          <meta name="robots" content="noindex, nofollow" />
          <meta name="fomo:group_url" content={TemplateGroupPresenter.viewTemplateUrl(templateGroup)} />
          <meta name="fomo:image_url" content={TemplateGroupPresenter.ghostThumbnail(templateGroup)} />
        </MetaTags>
      </Container>

      {messageModalVisible && <MakeMessageModal make={make} onClose={handleMessageModalClose} />}
      {shareModalVisible && (
        <ShareModal
          make={make}
          text={title}
          setShowCVPModalNext={setShowCVPModalNext}
          autoShowCVPModal={autoShowCVPModal}
          onClose={handleShareModalClose}
          templateGroup={templateGroup}
        />
      )}
      {joinModalVisible && <JoinModal templateGroup={templateGroup} onClose={closeJoinModal} />}
    </div>
  );
};

Make.propTypes = {
  templateGroup: TemplateGroupPresenter.shape(),
  make: MakePresenter.shape(),
  loading: PropTypes.bool.isRequired,
  joinable: PropTypes.bool,
  isMessageModalVisible: PropTypes.bool,
  isShareModalVisible: PropTypes.bool,
};

Make.defaultProps = {
  joinable: false,
  isMessageModalVisible: false,
  isShareModalVisible: false,
};

export default Make;
